import { authorizePaymentMethod as upstreamAuthorizePaymentMethod } from 'customer-flows/gql/mutations/authorize-payment-method'
import stripeProcessorManager from 'customer-flows/utils/stripe-processor-manager'
import config from 'customer-flows/config/environment'
import Logger from 'customer-flows/utils/logger'
import { KLARNA_PAYMENT_METHOD_TYPE } from 'customer-flows/utils/stripe-payment-method-types'
import withTimeout from 'customer-flows/utils/with-timeout'
import deferred from 'customer-flows/utils/deferred'

const AUTHORIZE_ERROR_CODES: CreateOrderFromQuoteErrorCode[] = [
  'NO_LONGER_AVAILABLE',
  'ALREADY_PROCESSED',
  'DUPLICATE_MEMBERSHIP_PURCHASE'
]

const CAPTURE_STATUS_CHECK_TIMEOUT_MS = 4000
const MAX_CAPTURE_STATUS_CHECK_FAILURES = 6

type KlarnaAuthorization =
  | { status: 'authorized' }
  | { status: 'declined'; message?: string }
  | { status: 'card-issuer-discount-ineligible' }
  | { status: 'error'; errorCode: CreateOrderFromQuoteErrorCode; message: string }

type KlarnaTabCloseCheck = { outcome: 'captured' } | { outcome: 'not_captured' } | { outcome: 'check_failed'; error: unknown }

export default function paymentKlarna({ stores, callerContext }: CommandArgs) {
  const accountStore = stores['account']
  const quoteStore = stores['quote']
  const paymentStore = stores['payment']

  async function authorize(clientSecret: string): Promise<KlarnaAuthorization> {
    const authRes = await upstreamAuthorizePaymentMethod(callerContext, {
      input: {
        quoteId: quoteStore.id,
        idempotencyKey: `${quoteStore.id}-${clientSecret}-`,
        paymentMethod: KLARNA_PAYMENT_METHOD_TYPE,
        token: clientSecret
      }
    })

    const authorization = authRes?.authorizePaymentMethod

    if (!authorization) {
      throw new Error('No response from authorizePaymentMethod')
    }

    if (authorization.errorCode === 'DECLINED') {
      return { status: 'declined', message: authorization.consumerMessage || authorization.message }
    }

    if (authorization.message) {
      const errorCode = AUTHORIZE_ERROR_CODES.includes(authorization.message as CreateOrderFromQuoteErrorCode)
        ? (authorization.message as CreateOrderFromQuoteErrorCode)
        : 'UNKNOWN'
      return { status: 'error', errorCode, message: authorization.message }
    }

    if (authorization.cardIssuerDiscountAmount) {
      return { status: 'card-issuer-discount-ineligible' }
    }

    return { status: 'authorized' }
  }

  async function confirmAndGetAuthorizationUrl(): Promise<{ paymentIntentId: string; authorizationUrl: string }> {
    const stripeData = accountStore.stripeData
    if (!stripeData?.publishableKey || !stripeData?.type) {
      throw new Error('Stripe configuration not available for Klarna')
    }

    const stripeProcessor = (await stripeProcessorManager.getProcessor({
      publishableKey: stripeData.publishableKey,
      type: stripeData.type,
      accountId: stripeData.accountId
    })) as any

    const { error, paymentIntent } = await stripeProcessor.processorAPI.confirmPayment({
      elements: stripeProcessor.stripeElements,
      handleActions: false,
      confirmParams: {
        return_url: `${config.paymentEventsServer}/payment_callback_processed/${quoteStore.id}`
      }
    })

    if (error) throw error

    const authorizationUrl = paymentIntent?.next_action?.redirect_to_url?.url
    if (!authorizationUrl) {
      throw new Error('No Klarna authorization link returned')
    }

    return { paymentIntentId: paymentIntent.id, authorizationUrl }
  }

  async function isPaymentIntentCaptured(clientSecret: string): Promise<boolean> {
    const stripeData = accountStore.stripeData
    if (!stripeData?.publishableKey || !stripeData?.type) return false

    const stripeProcessor = await stripeProcessorManager.getProcessor({
      publishableKey: stripeData.publishableKey,
      type: stripeData.type,
      accountId: stripeData.accountId
    })

    const { paymentIntent } = await withTimeout(stripeProcessor.retrievePaymentIntent(clientSecret), CAPTURE_STATUS_CHECK_TIMEOUT_MS)

    return paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing'
  }

  async function checkTabCloseOutcome(clientSecret: string): Promise<KlarnaTabCloseCheck> {
    try {
      const captured = await isPaymentIntentCaptured(clientSecret)
      return captured ? { outcome: 'captured' } : { outcome: 'not_captured' }
    } catch (error) {
      return { outcome: 'check_failed', error }
    }
  }

  // Catches the customer closing the tab themselves, which noreaga has no way to observe.
  function watchForTabClosed(clientSecret: string): { promise: Promise<never>; cancel: () => void } {
    let cancelled = false
    let checking = false
    let consecutiveCheckFailures = 0

    const outcome = deferred<never>()

    function reportNotCompleted() {
      outcome.reject(new Error('not_completed' satisfies AsyncPaymentCaptureReason))
    }

    async function pollOnce() {
      checking = true
      const result = await checkTabCloseOutcome(clientSecret)
      checking = false

      if (cancelled) return

      if (result.outcome === 'captured') {
        consecutiveCheckFailures = 0
        cancel()
        return
      }

      if (result.outcome === 'not_captured') {
        reportNotCompleted()
        return
      }

      consecutiveCheckFailures++
      Logger.warn('Payment: failed to check Klarna payment intent status', { error: result.error, attempt: consecutiveCheckFailures })

      if (consecutiveCheckFailures >= MAX_CAPTURE_STATUS_CHECK_FAILURES) {
        reportNotCompleted()
      }
    }

    const intervalId = setInterval(() => {
      if (cancelled || checking || !paymentStore.preOpenedKlarnaTab?.closed) return

      pollOnce()
    }, 500)

    function cancel() {
      cancelled = true
      clearInterval(intervalId)
    }

    return { promise: outcome.promise, cancel }
  }

  return { authorize, confirmAndGetAuthorizationUrl, watchForTabClosed }
}

declare global {
  export type PaymentKlarnaCommands = ReturnType<typeof paymentKlarna>

  interface CommandRegistry {
    'payment/klarna': PaymentKlarnaCommands
  }
}
